import { annotationFilterStatuses } from '@dermloop/static-values';
import type {
  AnnotationFilterStatus,
  AnnotationTool,
} from '@dermloop/static-values';
import { Exclude } from 'class-transformer';
import {
  IsBoolean,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from '../base.dto';
import { ImageCriteriaDto } from '../diagnoses/image-criteria.dtos';
import { UserDto } from '../user.dto';

class AnnotationFilterRange {
  @IsOptional()
  @IsNumber()
  min?: number;
  @IsOptional()
  @IsNumber()
  max?: number;
}

/**
 * Dto for requesting annotation list dtos.
 */
export class AnnotationListDto {
  @IsOptional()
  @IsUUID('all', { each: true })
  selectedDiagnosisIds?: string[];

  @IsOptional()
  selectedDifficulty?: AnnotationFilterRange;

  @IsOptional()
  @IsUUID('all', { each: true })
  selectedLocationIds?: string[];

  @IsOptional()
  selectedRaters?: AnnotationFilterRange;

  @IsOptional()
  @IsIn(annotationFilterStatuses)
  selectedStatus?: AnnotationFilterStatus;

  @IsOptional()
  @Min(0)
  skip?: number;

  @IsOptional()
  @Max(200)
  @Min(1)
  take? = 10;
}

/**
 * Dto for containing data that was requested.
 */
export interface AnnotationListResultDto {
  // Training condition id
  trainingConditionId: string;
  // True = approved. false = annotated but not approved. undefined = not annotated
  approved?: boolean;
  skipped: boolean;
  diagnosisName: string;
  locationName: string;
  difficulty?: number;
}

/**
 * Overall details for the annotation page. Does not include the actual annotations,
 * but all supporting data.
 */
export interface TrainingConditionAnnotationOverviewDto {
  trainingConditionId: string;
  dermoscopicImageId: string;
  dermoscopicImageUrl: string;
  clinicalImageId: string;
  clinicalImageUrl: string;
  clinicalImageFocalPointX: number;
  clinicalImageFocalPointY: number;
  patientAgeYears: number;
  locationName: string;
  gender: string;
  difficulty?: number;
  diagnosisId: string;
  diagnosisName: string;
  formerRatersTotalCount?: number;
  classicCriteria?: ImageCriteriaDto[];
  annotations: AnnotationDto[];
}

/**
 * Data for displaying how many that has answered a specific diagnosis for the training condition.
 */
export interface FormerRaterDto {
  diagnosisGroupName: string;
  answerCount: number;
}

/**
 * The annotation for the training condition.
 */
export interface AnnotationDto extends BaseDto {
  createdByUser: UserDto;
  created: Date;
  approved?: boolean;
  skipped: boolean;
  reviewedBy?: UserDto;
  criteriaAnnotations?: CriteriaAnnotationDto[];
  comment?: string;
  rois?: CriteriaAnnotationAreaDto[];

  dermoscopicImageWidth: number;
  dermoscopicImageHeight: number;
  clinicalImageWidth: number;
  clinicalImageHeight: number;

  dermoscopicImageId: string;
  clinicalImageId: string;
}

/**
 * DTO used for creating an annotation for a training condition.
 */
export class CreateAnnotationDto
  extends BaseCreateDto
  implements Partial<AnnotationDto>
{
  @IsNotEmpty()
  @IsUUID()
  trainingConditionId: string;

  // The id of the dermoscopic image that was annotated. Stored in case the image is changed for a training condition
  @IsNotEmpty()
  @IsUUID()
  trainingConditionDermoscopicImageId: string;

  // The id of the clinical image that was annotated. Stored in case the image is changed for a training condition
  @IsNotEmpty()
  @IsUUID()
  trainingConditionClinicalImageId: string;

  // Optional, as there might not be any criteria, but user has added a comment
  @IsOptional()
  @ValidateNested()
  createCriteriaAnnotations?: CreateCriteriaAnnotationDto[];

  rois?: CriteriaAnnotationAreaDto[];

  @IsOptional()
  @IsString()
  comment?: string;

  @IsNumber()
  dermoscopicImageWidth: number;

  @IsNumber()
  dermoscopicImageHeight: number;

  @IsNumber()
  clinicalImageWidth: number;

  @IsNumber()
  clinicalImageHeight: number;

  @IsOptional()
  @IsBoolean()
  skip?: boolean;

  @Exclude()
  approved?: never;

  @Exclude()
  reviewedBy?: never;
}

export class UpdateAnnotationDto
  extends BaseUpdateDto
  implements Partial<AnnotationDto>
{
  @IsBoolean()
  approved: boolean;

  @Exclude()
  criteriaAnnotations?: never[];

  @Exclude()
  comment?: never;

  @Exclude()
  dermoscopicImageWidth?: never;

  @Exclude()
  dermoscopicImageHeight?: never;

  @Exclude()
  clinicalImageWidth: never;

  @Exclude()
  clinicalImageHeight: never;

  @Exclude()
  reviewedBy?: never;

  @Exclude()
  trainingConditionDermoscopicImageId: never;

  @Exclude()
  trainingConditionClinicalImageId: never;
}

export class CreateCriteriaAnnotationDto
  extends BaseUpdateDto
  implements Partial<CriteriaAnnotationDto>
{
  @IsBoolean()
  annotate: boolean;

  @IsUUID()
  imageId: string;

  tool?: AnnotationTool;

  // Normalized data when coming from API. Absolute points when working with data in frontend
  @ValidateNested()
  areas?: CriteriaAnnotationAreaDto[];

  // Normalized data when coming from API. Absolute points when working with data in frontend
  @ValidateNested()
  arrows?: CriteriaAnnotationArrowDto[];

  @IsUUID()
  imageCriteriaId: string;
}

export interface CriteriaAnnotationDto extends BaseDto {
  annotate: boolean;

  imageId: string;

  tool?: AnnotationTool;

  // Normalized data when coming from API. Absolute points when working with data in frontend
  areas?: CriteriaAnnotationAreaDto[];

  arrows?: CriteriaAnnotationArrowDto[];

  imageCriteria: ImageCriteriaDto;
}

/**
 * The annotation points should always be saved as percentage of the training condition image width, x, and height, y.
 * This is why min is 0 for 0% of the image height/width or 1 for 100% of the image height/width
 */
export class AnnotationPointDto {
  @Min(0)
  @Max(1)
  x: number;

  @Min(0)
  @Max(1)
  y: number;
}

export class CriteriaAnnotationArrowDto {
  @IsString()
  id: string;
  @ValidateNested()
  focalPoint: AnnotationPointDto;
  @ValidateNested()
  rotatePoint: AnnotationPointDto;
}

export class CriteriaAnnotationAreaDto {
  @IsString()
  id: string;
  @ValidateNested()
  points: AnnotationPointDto[];
}

export interface AnnotationFeedbackDto extends BaseDto {
  feedbackNote?: string;
  markedAsRead: boolean;
  changesRequested: boolean;
  annotationId: string;
  id: string;
  createdByUser: UserDto;
}

export class CreateAnnotationFeedbackDto
  extends BaseCreateDto
  implements Partial<AnnotationFeedbackDto>
{
  @IsOptional()
  @IsString()
  feedbackNote?: string;

  @IsBoolean()
  markedAsRead: boolean;

  @IsBoolean()
  changesRequested: boolean;

  @IsUUID()
  annotationId: string;
}

export class UpdateAnnotationFeedbackDto
  extends BaseUpdateDto
  implements Partial<AnnotationFeedbackDto>
{
  @IsOptional()
  @IsString()
  feedbackNote?: string;

  @IsOptional()
  @IsBoolean()
  markedAsRead?: boolean;

  @IsOptional()
  @IsBoolean()
  changesRequested?: boolean;

  @Exclude()
  annotationId?: never;
}

/**
 * Interface representing a collection of arrows. Ids are solely used for display purposes.
 */
export interface CriteriaAnnotationArrowCollectionDto {
  [arrowId: string]: CriteriaAnnotationArrowDto;
}

/**
 * Interface representing a collection of areas½. Ids are solely used for display purposes.
 */
export interface CriteriaAnnotationAreaCollectionDto {
  [areaId: string]: CriteriaAnnotationAreaDto;
}
